// /accept-invite?token=... — Flow A invitation acceptance.
//
// Two-step UI:
//   1. Preview the invitation (shows the pre-filled email so the
//      customer can confirm they're signing up under the right
//      address; the email field is locked).
//   2. Set a password; on submit, the backend creates the
//      CustomerUser with email_verified_at = now AND
//      contact_link_approved = true, issues tokens, and the SPA
//      lands signed-in.
//
// Tier pricing applies immediately on the first pricing request —
// the spec says the invitation is the approval.

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

export function AcceptInvite(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { t } = useTranslation(['auth', 'common']);

  const [preview, setPreview] = useState<authApi.InvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const result = await authApi.previewInvitation(token);
        setPreview(result);
      } catch (err) {
        setPreviewError(extractApiError(err)?.message ?? t('acceptInvite.invalidInvitation.defaultMessage'));
      }
    })();
  }, [token, t]);

  if (!token) return <Navigate to="/login" replace />;

  if (previewError) {
    return (
      <div className="card">
        <h2>{t('acceptInvite.invalidInvitation.heading')}</h2>
        <div className="banner banner--error">
          <strong>{previewError}</strong>
        </div>
        <p>
          {t('acceptInvite.invalidInvitation.needNewInvitation')}{' '}
          <Trans
            i18nKey="acceptInvite.invalidInvitation.alreadyHaveAccount"
            t={t}
            values={{ signIn: t('common:nav.signIn') }}
            components={[<Link key="signin" to="/login" />]}
          />
        </p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="card">
        <p>{t('acceptInvite.checkingInvitation')}</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('acceptInvite.title')}</h2>
      <p>
        <Trans
          i18nKey="acceptInvite.intro"
          t={t}
          values={{ email: preview.email }}
          components={[<strong key="email" />]}
        />
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMessage(null);
          setSuggestions(null);
          if (password !== confirm) {
            setErrorMessage(t('acceptInvite.errors.mismatch'));
            return;
          }
          setSubmitting(true);
          void (async () => {
            try {
              await authApi.acceptInvitation({ token, password });
              // The accept call set the access token in memory; pull
              // /me into the AuthContext so the layout updates.
              await refresh();
              navigate('/quotes', { replace: true });
            } catch (err) {
              const envelope = extractApiError(err);
              setErrorMessage(envelope?.message ?? t('acceptInvite.errors.acceptFailed'));
              const sugg = envelope?.details?.suggestions;
              if (Array.isArray(sugg)) setSuggestions(sugg as string[]);
              setSubmitting(false);
            }
          })();
        }}
      >
        <div className="form-row">
          <label htmlFor="email">{t('acceptInvite.fields.email.label')}</label>
          <input id="email" type="email" value={preview.email} readOnly disabled />
        </div>
        <div className="form-row">
          <label htmlFor="password">{t('acceptInvite.fields.password.label')}</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="help">{t('acceptInvite.fields.password.help')}</span>
        </div>
        <div className="form-row">
          <label htmlFor="confirm">{t('acceptInvite.fields.confirm.label')}</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {errorMessage && (
          <div className="banner banner--error">
            <strong>{errorMessage}</strong>
            {suggestions && suggestions.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {suggestions.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? t('acceptInvite.actions.submitting') : t('acceptInvite.actions.submit')}
        </button>
      </form>
    </div>
  );
}
